import { Button, Modal as BootstrapModal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState, useEffect } from 'react';
import Moment from 'moment';
import Deployment from "../../services/deployment";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";
import FileTreeView from "../controls/FileTreeView";
import ListStatus from "./forms/controls/ListStatus";

const DATE_MOMENT_FORMAT = "DD-MMM-yyyy HH:mm";

const OverrideHistoryModal = ({ deployment, onCancel }) => {
    const [translate] = useTranslation();
    const [diffData, setDiffData] = useState({});
    const [userSelectedFile, setUserSelectedFile] = useState('');
    const [selectedFile, setSelectedFile] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasDownloadError, setHasDownloadError] = useState(false);
    const [selectedDiffVersion, setSelectedDiffVersion] = useState('');

    const getCustomerConfigDiff = useCallback(async (customerConfigVersion) => {
        if (diffData[customerConfigVersion]) {
            setSelectedDiffVersion(customerConfigVersion);
            return;
        }

        setIsLoading(true);
        const response = await Deployment.getCustomerConfigDiff(deployment._id, customerConfigVersion);
        if (response && !response.error) {
            setDiffData(prev => {
                prev[customerConfigVersion] = response.data;
                return prev;
            });
            setSelectedDiffVersion(customerConfigVersion);
        }
        else {
            setHasDownloadError(true);
        }

        setIsLoading(false);
    }, [diffData, deployment._id]);

    useEffect(() => {
        if (userSelectedFile) {
            if (diffData[selectedDiffVersion]?.fileList.find(file => file.path === userSelectedFile)) {
                setSelectedFile(userSelectedFile);
                return;
            }
        }
        setSelectedFile(diffData[selectedDiffVersion]?.fileList[0]?.path)
    }, [diffData, selectedDiffVersion, userSelectedFile]);

    const renderAdditionalInfo = useCallback((item) => {
        let uploaded = translate("OTM_UPLOADED_INFO")
            .split("{0}")
            .join(item.user?.email || translate("OTM_UNKNOWN_USER"));

        return <label className="d-block small">{uploaded}</label>;
    }, [translate]);

    const renderVersions = useMemo(() => {
        if (deployment.customerConfig && deployment.customerConfig.length > 0) {
            return (
                <div className="overflow-hidden d-flex flex-column modal-body-scroll">
                    <div className="list-group flex-grow-1 overflow-x-hidden overflow-y-auto padded-scrollbar">
                        {deployment.customerConfig.slice().reverse().map((item, index) => (
                            <div className={`list-group-item small p-3 ${item.version === selectedDiffVersion ? "active" : ""}`} key={index}>
                                <div className="row mt-n2 align-items-center justify-content-end">
                                    <div className="col mt-2">
                                        <label className="d-block h6">{Moment(item.updatedAt).format(DATE_MOMENT_FORMAT)}</label>
                                        {renderAdditionalInfo(item)}
                                        <label className="d-block small">{item.version}</label>
                                    </div>
                                    {item.version !== selectedDiffVersion && <>
                                        <div className="col-12 d-lg-none"></div>
                                        <div className="col-auto mt-2">
                                            <div className="row gx-2 mt-n2 align-items-center justify-content-end">
                                                <div className={`col-auto mt-2`}>
                                                    <Button
                                                        size="sm"
                                                        variant="primary"
                                                        onClick={() => getCustomerConfigDiff(item.version)}
                                                    >{translate("OTM_SELECT")}</Button>
                                                </div>
                                            </div>
                                        </div>
                                    </>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )
        }
        else {
            return <ListStatus loading={false} error={false} empty={true} emptyText={translate("OTM_NO_VERSIONS")} ></ListStatus>;
        }
    }, [deployment, translate, renderAdditionalInfo, getCustomerConfigDiff, selectedDiffVersion]);

    const renderFiles = useMemo(() => {
        if (diffData[selectedDiffVersion] && diffData[selectedDiffVersion].fileList.length > 0) {
            return <div className="row h-lg-100">
                <div className="col-3 h-lg-100">
                    <div className="overflow-hidden d-flex flex-column modal-body-scroll h-lg-100">
                        <div className="flex-grow-1 overflow-y-auto">
                            <FileTreeView
                                selectedFile={selectedFile}
                                files={diffData[selectedDiffVersion].fileList}
                                onSelect={(file) => setUserSelectedFile(file)}
                            />
                        </div>
                    </div>
                </div>
                <div className="col-9">
                    <div className="overflow-hidden d-flex flex-column modal-body-scroll h-lg-100">
                        <div className="flex-grow-1 overflow-y-auto border file-diff-container">
                            <ReactDiffViewer
                                oldValue={diffData[selectedDiffVersion].oldValue[selectedFile] || ""}
                                newValue={diffData[selectedDiffVersion].newValue[selectedFile] || ""}
                                compareMethod={DiffMethod.WORDS}
                                showDiffOnly={true}
                                useDarkTheme={false}
                                leftTitle={selectedFile}
                                splitView={false}
                                hideLineNumbers={true}
                            />
                        </div>
                    </div>
                </div>
            </div>;
        }
        else {
            return <ListStatus 
                loading={isLoading} 
                loadingText={translate("OTM_LOADING")} 
                error={hasDownloadError} 
                empty={true} 
                emptyIcon="InfoOutlined" 
                emptyText={translate(!diffData[selectedDiffVersion] ? "OTM_SELECT_VERSION_VIEW_FILES" : "OTM_NO_VERSION_OVERRIDE_FILES")} >
            </ListStatus>;
        }
    }, [isLoading, hasDownloadError, selectedDiffVersion, selectedFile, diffData, translate]);

    return (
        <BootstrapModal size={"xl"} show={true} backdrop="static" centered>
            <BootstrapModal.Header className="h5 text-secondary">
                {translate("OTM_OVERRIDE_HISTORY")}
            </BootstrapModal.Header>
            <BootstrapModal.Body>
                <div className="row mt-n3">
                    <div className="col-12 col-lg-4 mt-3">
                        {renderVersions}
                    </div>
                    {deployment.customerConfig && deployment.customerConfig.length > 0 && <div className="col-12 col-lg-8 mt-3">
                        {renderFiles}
                    </div>}
                </div>
            </BootstrapModal.Body>
            <BootstrapModal.Footer>
                <Button variant="secondary" onClick={() => onCancel()} className="btn-sm">{translate("OTM_CLOSE")}</Button>
            </BootstrapModal.Footer>
        </BootstrapModal>
    )
}

export default OverrideHistoryModal;